import React from 'react';
import { 
  Typography, 
  Box,
  Divider,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';

const AboutPage: React.FC = () => {
  const teamMembers = [
    {
      name: 'Alex Johnson',
      role: 'Founder & CEO',
      bio: 'Alex founded 1TimeMail in 2022 with a vision to protect online privacy for everyone. With over 12 years of experience in cybersecurity at companies like Cloudflare and Microsoft, he leads our mission to create the most secure temporary email service.',
      avatar: 'A'
    },
    {
      name: 'Sarah Chen',
      role: 'CTO',
      bio: 'Sarah joined 1TimeMail in early 2023. With a Ph.D. in Computer Science and background in distributed systems and security at Google, she ensures our platform remains robust, secure, and cutting-edge.',
      avatar: 'S'
    },
    {
      name: 'Miguel Rodriguez',
      role: 'Head of Design',
      bio: 'Miguel leads our design team with a focus on creating beautiful, intuitive user experiences. Previously at Stripe and Airbnb, his work ensures that 1TimeMail is not only secure but also a pleasure to use.',
      avatar: 'M'
    },
    {
      name: 'Aisha Patel',
      role: 'Privacy Officer',
      bio: 'Aisha joined us in September 2023 with a background in tech law. Her expertise in data protection regulations including GDPR and CCPA helps keep 1TimeMail at the forefront of privacy-first services.',
      avatar: 'A'
    }
  ];

  return (
    <PageTemplate 
      title="About Us" 
      description="Learn about our mission to protect online privacy with 1TimeMail's temporary email service."
      breadcrumbs={[{ label: 'About Us', path: '/about' }]}
    >
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Our Mission
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          At 1TimeMail, we believe that privacy is a fundamental right. Our mission is to provide everyone with a simple, secure way to protect their digital identity.
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Founded in 2022, 1TimeMail was born out of frustration with the increasing amount of personal data being collected through email signups. We created a solution that allows you to engage online without compromising your privacy.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Since our launch in April 2022, we've grown rapidly and now serve hundreds of thousands of users monthly who trust us to protect their privacy with our temporary email service. We're proud to be at the forefront of privacy technology, constantly innovating to stay ahead of evolving threats.
        </Typography>
      </Box>

      <Divider sx={{ my: 6 }} />

      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 4 }}>
          Our Team
        </Typography>
        
        <Grid container spacing={3}>
          {teamMembers.map((member, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: 'primary.main',
                        width: 60,
                        height: 60,
                        fontSize: '1.5rem'
                      }}
                    >
                      {member.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {member.name}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        {member.role}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {member.bio}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Our Values
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Privacy First
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We believe your personal information belongs to you. Everything we do is designed with privacy as the top priority, from our data handling to our retention policies.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Simplicity
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Privacy tools should be accessible to everyone. We create technology that's powerful yet simple enough for anyone to use, regardless of technical background.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Transparency
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We're open about how our service works and how we handle data. Our privacy policy and terms are written in clear language, and we're always available to answer questions.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </PageTemplate>
  );
};

export default AboutPage; 